import type { AxiosError } from "axios";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import type { QueryClient } from "react-query";

/**
 * Forwards uncaught errors and unhandled rejections to Sentry with their
 * source location. Idempotent: assigning the handlers again is a no-op.
 * Runs at module scope on the client for both the Pages Router (`_app.tsx`)
 * and the App Router shell (`AppProviders`).
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;

  window.onerror = (message, source, lineno, colno, error) => {
    Sentry.captureException(error, {
      extra: { message, source, lineno, colno },
    });
  };

  window.onunhandledrejection = (event) => {
    Sentry.captureException(event.reason, {
      extra: { type: "unhandledrejection", reason: event.reason },
    });
  };
}

/**
 * Reports an error that reached the top-level boundary to Sentry and
 * PostHog with the signed-in user, active org, and HTTP failure details from
 * the query cache, so incidents can be split by user, org, and status class.
 */
export function reportBoundaryError(error: Error, queryClient: QueryClient) {
  const axiosError = error as AxiosError;
  const activeGithubOrg =
    (
      queryClient.getQueryData("connectedGithubOrgs") as
        | { active_org_name?: string | null }
        | undefined
    )?.active_org_name ?? null;
  const user = queryClient.getQueryData("user") as
    | { id: string; name: string; email: string }
    | undefined;

  Sentry.withScope((scope) => {
    if (user?.id && user?.name) {
      scope.setUser({
        id: user.id,
        username: user.name,
        email: user.email || undefined,
      });
    }

    if (activeGithubOrg && typeof activeGithubOrg === "string") {
      scope.setTag("organization", activeGithubOrg);
      scope.setExtra("activeGithubOrg", activeGithubOrg);
    }

    if (axiosError.response) {
      scope.setExtra("error_response", axiosError.response.data);
      scope.setExtra("error_status", axiosError.response.status);
      scope.setExtra("error_headers", axiosError.response.headers);
    }
    if (axiosError.config) {
      scope.setExtra("error_config", axiosError.config);
    }

    const status = axiosError.response?.status;
    if (status && status >= 500) {
      scope.setLevel("error");
    } else if (status && status >= 400) {
      scope.setLevel("warning");
    }

    Sentry.captureException(error);
  });

  posthog.capture("error", {
    error,
    orgName: activeGithubOrg,
    stackTrace: error.stack || undefined,
  });
}
